import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { SelectArgs } from '@trimble-gcs/i18n-types';
import {
  ModusButtonModule,
  ModusIconModule,
  ModusMenu,
  ModusMenuModule,
  ModusMenuTrigger,
} from '@trimble-gcs/modus';
import { filter } from 'rxjs';
import { CloseSortMenu } from '../../scan-3d-panel/scan-3d.actions';
import { SortInfo } from '../../scandata/scandata-query.models';
import { ScandataModel } from '../../scandata/scandata.models';
import { scandataSortMenuTranslations } from './scandata-sort-menu.translations';

export interface SortOption {
  sortableProperty: SortableProperty;
  direction?: 'asc' | 'desc';
  active: boolean;
}

export interface SortableProperty {
  property: keyof ScandataModel;
  displayName: string;
}

const sortableProperties: SortableProperty[] = [
  { property: 'name', displayName: 'Name' },
  { property: 'uploadedDate', displayName: 'Upload Date' },
  { property: 'captureDate', displayName: 'Capture Date' },
];

@UntilDestroy()
@Component({
  selector: 'sd-scandata-sort-menu',
  imports: [ModusIconModule, ModusButtonModule, ModusMenuModule],
  templateUrl: './scandata-sort-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScandataSortMenuComponent {
  private readonly translationService = inject(TranslationService);
  private readonly actions$ = inject(Actions);

  private readonly menu = viewChild.required(ModusMenu);
  private readonly menuTrigger = viewChild.required(ModusMenuTrigger);

  readonly sortInfo = input.required<SortInfo>();
  readonly onSortClick = output<SortInfo>();

  readonly menuItems = computed(() => this.getMenuItems(this.sortInfo()));

  constructor() {
    this.subscribeToCloseSortMenu();
  }

  sortClick(sortOption: SortOption) {
    this.onSortClick.emit({
      sortBy: sortOption.sortableProperty.property,
      sortDirection: sortOption.direction === 'asc' ? 'desc' : 'asc',
    });
  }

  private subscribeToCloseSortMenu() {
    this.actions$
      .pipe(
        ofActionDispatched(CloseSortMenu),
        filter(() => this.menu().isOpen),
        untilDestroyed(this),
      )
      .subscribe(() => this.menuTrigger().closeMenu());
  }

  private getMenuItems(activeSort: SortInfo): SortOption[] {
    const selectMap = scandataSortMenuTranslations();

    return sortableProperties.map((sortableProperty) => {
      const direction =
        sortableProperty.property === activeSort.sortBy && activeSort.sortDirection !== ''
          ? activeSort.sortDirection
          : undefined;

      const { property } = sortableProperty;
      const selectArgs: SelectArgs = { select: property };
      const displayName = this.translationService.translateSelect(selectArgs, selectMap);

      return {
        sortableProperty: { property, displayName },
        direction,
        active: isDefined(direction),
      } satisfies SortOption;
    });
  }
}
